import * as THREE from 'three';
import type { Viewer } from '../viewer/viewer';
import { View } from '../viewer/View';
import { CameraMode } from '../defines';

/**
 * Programmatic-control shim that exposes the surface `RenderViewState`
 * expects on its `orbit` field, backed by the main viewer's existing
 * `OrbitControls` + `scene.view`. The existing `OrbitControls` keeps
 * handling user input (mouse, wheel, keyboard); the shim is only used
 * by tool code that wants to drive the main camera programmatically
 * through `RenderViewState.setCameraPose` / `setOrbitRadius` and friends.
 *
 * Why this exists: the widget's primitives (`focusOnWall`,
 * `setCameraPose`, custom view tabs) operate on a `RenderViewState`
 * which takes an orbit controller in its constructor. The widget uses
 * its own `WidgetOrbitController`; the main view's `OrbitControls` has
 * a different shape. This shim translates between the two so the main
 * view can have a `RenderViewState` and reuse the widget's primitives.
 *
 * Read-only fields:
 *  - `view` → the global `viewer.scene.view`. Mutations from
 *    `RenderViewState` propagate to the main camera every frame via
 *    the viewer's existing per-frame writeback in `viewer.update()`.
 *
 * Methods:
 *  - `setFromCamera(pos, target)` — replicates
 *    `WidgetOrbitController.setFromCamera` against the global view.
 *    The viewer's per-frame writeback picks up the new yaw/pitch/radius
 *    automatically.
 *  - `applyViewToCamera()` — no-op for the main view; the writeback
 *    runs on the next frame.
 *  - `setCamera(camera)` — routes to `viewer.setCameraMode(...)` so
 *    the active scene camera is swapped. `RenderViewState.applyCameraMode`
 *    builds a fresh THREE camera and calls this; we ignore the THREE
 *    instance and just toggle the mode on the viewer (the viewer owns
 *    `cameraP` / `cameraO` and picks the active one based on mode).
 */
export class MainOrbitControllerShim {
  private viewer: Viewer;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /** Live read-through to `viewer.scene.view`. */
  get view(): View {
    return this.viewer.scene.view;
  }

  /**
   * Initialize the view from a camera pose. Sets position, derives
   * yaw/pitch from the look direction (via the `View.direction` setter),
   * and stores the look distance as radius. Identical to
   * `WidgetOrbitController.setFromCamera`, plus a roll reset — the
   * widget version starts from a fresh `View` so roll is already 0,
   * but the main view's `scene.view.roll` carries whatever roll the
   * user had set previously. Without the reset, programmatic poses
   * inherit stale roll and the camera comes out tilted.
   */
  setFromCamera(position: THREE.Vector3, target: THREE.Vector3): void {
    const view = this.view;
    view.position.copy(position);
    const toTarget = new THREE.Vector3().subVectors(target, position);
    const radius = toTarget.length();
    if (radius > 1e-6) {
      view.direction = toTarget.normalize();
    }
    view.radius = Math.max(radius, 1e-3);
    view.roll = 0;
    // No applyToCamera() here — the main viewer's per-frame `update()`
    // copies view.pitch/yaw/roll/position to the active camera on its
    // next tick. Tools that need frame-immediate sync should request
    // a render after calling this.
  }

  /**
   * No-op for the main view. The widget calls this to force a
   * synchronous camera writeback after a pose change; on the main
   * viewer the writeback is per-frame in `viewer.update()` and there's
   * no public synchronous equivalent. Tools that need a guaranteed
   * synchronous view typically await one RAF tick.
   */
  applyViewToCamera(): void {
    // intentional no-op
  }

  /**
   * Set the orbit's radius, compensating for the main viewer's ortho
   * frustum formula. The widget renders ortho as `top/bottom = ±radius`,
   * while the main viewer renders `top/bottom = ±radius / aspect` (see
   * `viewer.ts` renderOneView for the main camera). Tools call
   * `setOrbitRadius(R)` expecting `top = R`; we multiply by aspect so
   * the main viewer's `±radius/aspect = ±R` produces the same on-screen
   * vertical span.
   */
  setOrbitRadius(radius: number): void {
    const aspect = this.canvasAspect();
    this.viewer.scene.view.radius = Math.max(1e-3, radius * aspect);
  }

  /** Inverse of setOrbitRadius: undo the aspect scaling to return `top = R`. */
  getOrbitRadius(): number {
    const aspect = this.canvasAspect();
    return this.viewer.scene.view.radius / Math.max(1e-3, aspect);
  }

  private canvasAspect(): number {
    const canvas = this.viewer.renderer.domElement;
    const width = canvas.clientWidth || 1;
    const height = canvas.clientHeight || 1;
    return width / Math.max(1, height);
  }

  /**
   * Switch the main camera's projection mode. The widget passes a
   * freshly constructed THREE camera here (built from the orbit pose);
   * for the main viewer we just toggle the camera mode on the viewer
   * itself — the viewer owns `cameraP` / `cameraO` and the active
   * camera is picked from `scene.cameraMode`.
   */
  setCamera(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
    const wantOrtho = (camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    this.viewer.setCameraMode(wantOrtho ? CameraMode.ORTHOGRAPHIC : CameraMode.PERSPECTIVE);
  }
}
